import { z } from 'zod';
/**
 * Access scopes for management API keys (KIR-4377).
 *
 * ## What a scope is, and what it is not
 *
 * A scope is a **ceiling on a key**, never a grant to it. Every `/api/v1`
 * request is gated twice and independently: the route's required scope must be
 * present on the key, *and* the existing role gate must pass. A key minted with
 * `accounts:write` by a manager still cannot remove an owner, because the role
 * gate is untouched by any of this.
 *
 * Before this existed, a key's authority was exactly its minter's roles — an
 * owner-minted key could invite accounts, flip policy modes and rewrite SIEM
 * settings, and the only way to narrow it was to have someone less privileged
 * mint it. Scopes make "read-only key" expressible without that contortion.
 *
 * ## The vocabulary is frozen
 *
 * These strings are a **public contract**, published in the OpenAPI document and
 * pasted into customer connector configs. Within `v1` a scope may be added but
 * never renamed or removed, exactly as `NAMING.md` requires of every other
 * published value. Adding a scope is additive; changing one is a new API
 * version.
 *
 * ## Granularity: product areas, not route files
 *
 * The groups are the areas the product's own navigation uses, because that is
 * how a customer deciding "what may this integration touch?" thinks — not
 * "which handler file serves it". Six resource files sit behind
 * {@link V1_SCOPE_INVENTORY} for that reason.
 *
 * Coarse is the safe direction to be wrong in: a group can later be **split**
 * additively (a key holding `inventory:read` would satisfy a future, narrower
 * `inventory-mcp:read`, because holding the broader scope is a superset), while
 * merging two published scopes into one would break every key that holds only
 * the other.
 */
/** The `alerts` scope group: alerts, mute, exclusions. */
export const V1_SCOPE_ALERTS = 'alerts';
/** The `audit-logs` scope group. Read-only — see {@link V1_WRITE_SCOPES}. */
export const V1_SCOPE_AUDIT_LOGS = 'audit-logs';
/**
 * The `inventory` scope group: MCP servers, extensions, plugins, AI
 * instructions, hooks and inventory users — everything the Inventory area of the
 * app shows — plus agent installer checksums (KIR-5145), which describe the
 * agent fleet rather than warrant a group of their own. Read-only.
 */
export const V1_SCOPE_INVENTORY = 'inventory';
/** The `shadow-ai` scope group: browser tools, installed tools, processes, summary. Read-only. */
export const V1_SCOPE_SHADOW_AI = 'shadow-ai';
/** The `policies` scope group: policies, their settings and their exceptions. */
export const V1_SCOPE_POLICIES = 'policies';
/** The `accounts` scope group: login accounts — invite, role change, removal. */
export const V1_SCOPE_ACCOUNTS = 'accounts';
/**
 * The `settings` scope group: organization settings, currently SIEM forwarding.
 *
 * Deliberately **not** folded into `policies`, even though both are
 * "configuration a manager changes". SIEM settings hold a webhook URL that is
 * itself a bearer credential, so an integration trusted to manage detection
 * policy must not acquire the ability to read or redirect the customer's audit
 * stream as a side effect.
 */
export const V1_SCOPE_SETTINGS = 'settings';
/**
 * Every resource group, in the order the create-key UI and the published scope
 * table list them: the two operational surfaces first, then inventory-style
 * reads, then the three configuration areas.
 */
export const V1_SCOPE_RESOURCES = [
    V1_SCOPE_ALERTS,
    V1_SCOPE_AUDIT_LOGS,
    V1_SCOPE_INVENTORY,
    V1_SCOPE_SHADOW_AI,
    V1_SCOPE_POLICIES,
    V1_SCOPE_ACCOUNTS,
    V1_SCOPE_SETTINGS,
];
/**
 * Display names for the resource groups.
 *
 * Lives here rather than in the frontend so the create-key dialog, the keys
 * list and the published scope table in the OpenAPI document all render the same
 * words — the single-source rule `NAMING.md` states for every other published
 * label.
 */
export const V1_SCOPE_RESOURCE_LABELS = Object.freeze({
    [V1_SCOPE_ALERTS]: 'Alerts',
    [V1_SCOPE_AUDIT_LOGS]: 'Audit logs',
    [V1_SCOPE_INVENTORY]: 'Inventory',
    [V1_SCOPE_SHADOW_AI]: 'Shadow AI',
    [V1_SCOPE_POLICIES]: 'Policies',
    [V1_SCOPE_ACCOUNTS]: 'Accounts',
    [V1_SCOPE_SETTINGS]: 'Settings',
});
/**
 * One-line explanations of what each group covers, for the create-key dialog and
 * the published scope table. Written for the person choosing, not for us.
 */
export const V1_SCOPE_RESOURCE_DESCRIPTIONS = Object.freeze({
    [V1_SCOPE_ALERTS]: 'Alerts, and muting or excluding them.',
    [V1_SCOPE_AUDIT_LOGS]: 'The organization audit trail.',
    [V1_SCOPE_INVENTORY]: 'MCP servers, extensions, plugins, AI instructions, hooks, monitored developers and agent installer checksums.',
    [V1_SCOPE_SHADOW_AI]: 'Browser AI tools, installed AI tools, running processes and their summary.',
    [V1_SCOPE_POLICIES]: 'Policy modes, policy settings and policy exceptions.',
    [V1_SCOPE_ACCOUNTS]: 'People holding a Kirin account — inviting, changing roles and removing them.',
    [V1_SCOPE_SETTINGS]: 'Organization settings, including SIEM forwarding.',
});
/**
 * Every read scope — one per resource group, since reading is possible on all of
 * them.
 */
export const V1_READ_SCOPES = [
    `${V1_SCOPE_ALERTS}:read`,
    `${V1_SCOPE_AUDIT_LOGS}:read`,
    `${V1_SCOPE_INVENTORY}:read`,
    `${V1_SCOPE_SHADOW_AI}:read`,
    `${V1_SCOPE_POLICIES}:read`,
    `${V1_SCOPE_ACCOUNTS}:read`,
    `${V1_SCOPE_SETTINGS}:read`,
];
/**
 * Every write scope.
 *
 * Only four, because only four groups have writes on this surface. There is
 * deliberately no `audit-logs:write`, `inventory:write` or `shadow-ai:write`:
 * the audit trail is append-only and written by the system, and inventory and
 * Shadow AI are observations the agent reports rather than state a customer
 * edits. Publishing a scope for an operation that does not exist would invite a
 * customer to grant an authority we cannot honour, and the day such a write
 * arrives the scope can be added additively.
 */
export const V1_WRITE_SCOPES = [
    `${V1_SCOPE_ALERTS}:write`,
    `${V1_SCOPE_POLICIES}:write`,
    `${V1_SCOPE_ACCOUNTS}:write`,
    `${V1_SCOPE_SETTINGS}:write`,
];
/** Every scope a key can hold, reads first. */
export const V1_SCOPES = [...V1_READ_SCOPES, ...V1_WRITE_SCOPES];
export const v1ScopeSchema = z.enum(V1_SCOPES).meta({
    id: 'V1Scope',
    title: 'Access scope',
    description: 'One `<resource>:<level>` permission on a management API key. A `:write` scope also confers its `:read`.',
});
/** The read scope for a resource group. Every group has one. */
export function readScopeFor(resource) {
    return `${resource}:read`;
}
/**
 * The write scope for a resource group, or `null` where the group has no writes.
 *
 * Derived from {@link V1_WRITE_SCOPES} rather than listed a second time, so a
 * group that gains writes needs one edit and the UI's third state appears on its
 * own.
 */
export function writeScopeFor(resource) {
    const candidate = `${resource}:write`;
    return V1_WRITE_SCOPES.includes(candidate)
        ? candidate
        : null;
}
/**
 * `alerts:read` → `alerts`. Scopes contain exactly one `:`, and the resource
 * half may itself be hyphenated (`audit-logs`), so this splits on the separator
 * rather than assuming a single word.
 */
export function scopeResource(scope) {
    return scope.slice(0, scope.indexOf(':'));
}
/**
 * Read scope → the write scope that implies it, where one exists.
 *
 * Derived from {@link V1_WRITE_SCOPES} so the implication table cannot fall out
 * of step with the scope list — a new write scope starts implying its read the
 * moment it is declared.
 */
const IMPLIED_BY = Object.freeze(Object.fromEntries(V1_WRITE_SCOPES.map(write => [readScopeFor(scopeResource(write)), write])));
/**
 * Does a key holding `granted` satisfy a route requiring `required`?
 *
 * The one rule beyond exact match is that **`X:write` confers `X:read`**. It is
 * the only implication, and it exists because the alternative is a UI where
 * granting write access to alerts without also ticking read produces a key that
 * can mute an alert it cannot fetch — a state no one wants and everyone would
 * eventually create by accident. Making it structural means the stored scope
 * list stays exactly what the customer chose, while the check is the one that
 * knows about the implication.
 *
 * Nothing else implies anything: no scope confers another resource's scope, and
 * `:read` never confers `:write`.
 */
export function scopeSatisfies(granted, required) {
    if (granted.includes(required)) {
        return true;
    }
    const implying = IMPLIED_BY[required];
    return implying !== undefined && granted.includes(implying);
}
/**
 * Every scope, as granted to a key with unrestricted access.
 *
 * Used for the pre-KIR-4377 keys, which are backfilled to full access so that a
 * customer's working integration does not start failing the day scopes ship, and
 * as the "Read & write" preset in the create dialog.
 */
export const V1_ALL_SCOPES = V1_SCOPES;
/**
 * Whether a scope list covers everything — the *Full access* badge in the keys
 * list, and what makes a legacy key indistinguishable from a deliberately
 * unrestricted one. That is intended: the two are equivalent in authority, and
 * storing "this one was grandfathered" would invite treating them differently
 * when the only useful advice for both is the same, namely to rotate to a
 * narrower key.
 */
export function isFullAccess(granted) {
    // Asked through `scopeSatisfies`, not by literal membership: a key granted
    // `alerts:write` *has* `alerts:read`, and the create dialog stores only the
    // write half for exactly that reason. Comparing membership would report the
    // "Read & write — every resource" preset as a partial, custom grant, which is
    // both wrong and alarming — it is the most powerful key the product mints.
    return V1_SCOPES.every(scope => scopeSatisfies(granted, scope));
}
/**
 * Whether a scope list confers no writes at all — the *Read-only* badge, and the
 * default the create dialog opens on.
 */
export function isReadOnly(granted) {
    return !granted.some(scope => V1_WRITE_SCOPES.includes(scope));
}
/**
 * Space-delimited rendering, for the OAuth token response's `scope` field
 * (RFC 6749 §5.1) and for the summary written onto the Descope key.
 */
export function formatScopeList(granted) {
    return granted.join(' ');
}
//# sourceMappingURL=scopes.js.map